import {
  Control,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
} from 'react-hook-form';
import { useEffect, useState } from 'react';
import copy from 'copy-to-clipboard';
import IconLoader from '../../icons/loader';
import formatDate from '../../../helpers/format-date';
import SidepanelTextRow from '../sidepanel-text-row';
import SidepanelSelectRow from '../sidepanel-select-row';
import SidepanelDateRow from '../sidepanel-date-row';
import { SelectItemProps } from '../../select/types';
import {
  Balance,
  BaseellaStatus,
  CASP,
  Currency,
  NostroTransaction,
  PaymentType,
} from '../types';
import SidepanelTextareaRow from '../sidepanel-textarea-row';
import SidepanelAutocompleteRow from '../sidepanel-autocomplete-row';
import IconRefresh from '../../icons/refresh';
import InputText from '../../inputs/input-text';
import ButtonPseudo from '../../button/button-pseudo';
import formatBalance from '../../../helpers/format-balance';
import SidepanelDefaultRow from '../../side-panel/side-panel-default-row';
import { Pagination, PermissionEnum } from '../../../base-types';
import SidepanelAmountRow from '../sidepanel-amount-row';
import IconCopy from '../../icons/copy-two';
import CheckboxInput from '../../checkbox/checkbox';
import DisableCreationOption from '../disable-creation-option/disable-creation-options';

import styles from '../transaction.module.css';

interface NostroGeneralContentProps {
  transactionInfo?: NostroTransaction;
  isLoading?: boolean;
  control?: Control;
  errors?: FieldErrors<NostroTransaction>;
  isEditable?: boolean;
  currencies?: SelectItemProps[];
  paymentsTypes?: SelectItemProps[];
  toAccounts?: SelectItemProps[];
  fromAccounts?: SelectItemProps[];
  toPartnerPSPs?: SelectItemProps[];
  fromPartnerPSPs?: SelectItemProps[];
  toAccountsIsLoading?: boolean;
  fromAccountsIsLoading?: boolean;
  toPartnerPSPsIsLoading?: boolean;
  fromPartnerPSPsIsLoading?: boolean;
  onSearchAutocomplete?: (name: string, search: string) => void;
  paymentTypeIsFilled?: boolean;
  toPartnerPspIsFilled?: boolean;
  fromPartnerPspIsFilled?: boolean;
  baseellaStatus?: BaseellaStatus;
  isCreateForm?: boolean;
  fromAccountBalance?: number;
  currencyId?: string;
  updateFromAccountBalance?: () => void;
  balanceFromAccountIsLoading?: boolean;
  toAccountBalance?: number;
  updateToAccountBalance?: () => void;
  balanceToAccountIsLoading?: boolean;
  activePermission?: PermissionEnum[];
  amount?: string;
  setAmount?: (data?: string) => void;
  currenciesList?: Currency[];
  fields?: any;
  overdraftEnabled?: boolean;
  fromPspAccountsList?: any[];
  toPspAccountsList?: any[];
  fromAccountBalanceInfo?: Balance;
  toAccountBalanceInfo?: Balance;
  caspList?: CASP[];
  paymentType?: string;
  fromPspAccountsPagination?: Pagination;
  fromPspAccountsNextPageIsLoading?: boolean;
  fromPspAccountsOnScroll?: () => void;
  toPspAccountsPagination?: Pagination;
  toPspAccountsNextPageIsLoading?: boolean;
  toPspAccountsOnScroll?: () => void;
  transactionId?: string;
  setValue?: UseFormSetValue<any>;
  purposeCodeVisible?: boolean;
  purposeCodes?: SelectItemProps[];
  purposeCodesIsLoading?: boolean;
  purposeCodesNextPageIsLoading?: boolean;
  onScrollPurposeCodes?: () => void;
  purposeCodesPagination?: Pagination;
  clearErrors?: UseFormClearErrors<NostroTransaction>;
  withTime?: {
    transferDateWithTime?: boolean;
    acceptedInWithTime?: boolean;
    acceptedOutWithTime?: boolean;
  };
  setWithTime?: {
    setTransferDateWithTime: (data: boolean) => void;
    setAcceptedInWithTime: (data: boolean) => void;
    setAcceptedOutWithTime: (data: boolean) => void;
  };
}

export default function NostroGeneralContent({
  transactionInfo,
  isLoading,
  control,
  currencies,
  paymentsTypes,
  errors,
  isEditable,
  onSearchAutocomplete,
  toAccounts,
  fromAccounts,
  toPartnerPSPs,
  fromPartnerPSPs,
  toAccountsIsLoading,
  fromAccountsIsLoading,
  toPartnerPSPsIsLoading,
  fromPartnerPSPsIsLoading,
  paymentTypeIsFilled,
  toPartnerPspIsFilled,
  fromPartnerPspIsFilled,
  isCreateForm,
  currencyId,
  updateFromAccountBalance,
  fromAccountBalance,
  balanceFromAccountIsLoading,
  toAccountBalance,
  updateToAccountBalance,
  balanceToAccountIsLoading,
  activePermission,
  amount,
  setAmount,
  currenciesList,
  baseellaStatus,
  fields,
  overdraftEnabled,
  fromPspAccountsList,
  toPspAccountsList,
  fromAccountBalanceInfo,
  toAccountBalanceInfo,
  caspList,
  paymentType,
  fromPspAccountsPagination,
  fromPspAccountsNextPageIsLoading,
  fromPspAccountsOnScroll,
  toPspAccountsPagination,
  toPspAccountsNextPageIsLoading,
  toPspAccountsOnScroll,
  transactionId,
  setValue,
  purposeCodeVisible,
  purposeCodes,
  purposeCodesIsLoading,
  purposeCodesNextPageIsLoading,
  onScrollPurposeCodes,
  purposeCodesPagination,
  clearErrors,
  withTime,
  setWithTime,
}: NostroGeneralContentProps): React.JSX.Element {
  const [copyIsSuccess, setCopyIsSuccess] = useState(false);
  const [infoValue, setInfoValue] = useState('');
  const [accountType, setAccountType] = useState<'internal' | 'undefined'>();
  const [accountNumber, setAccountNumber] = useState('');

  const onCopy = (copyValue: string) => {
    setCopyIsSuccess(true);
    copy(copyValue);

    setTimeout(() => {
      setCopyIsSuccess(false);
    }, 2000);
  };

  useEffect(() => {
    if (transactionInfo && transactionInfo.info) {
      setInfoValue(transactionInfo.info);
    }
  }, [transactionInfo]);

  useEffect(() => {
    if (transactionInfo && transactionInfo.senderAccountNumber) {
      setAccountNumber(transactionInfo.senderAccountNumber);
    }
  }, [transactionInfo]);

  useEffect(() => {
    if (!transactionInfo && !transactionId && !accountType) {
      setAccountType('internal');
    }
  }, [transactionInfo, transactionId, accountType]);

  useEffect(() => {
    if (
      transactionInfo &&
      transactionInfo.senderAccountNumber &&
      fromAccounts?.find(
        (item) => item.value === transactionInfo.senderAccountNumber,
      ) &&
      !accountType
    ) {
      setAccountType('internal');
    }
  }, [transactionInfo, accountType, fromAccounts]);

  useEffect(() => {
    if (
      transactionInfo &&
      !transactionInfo?.senderAccount &&
      baseellaStatus === BaseellaStatus.UNDEFINED_FUNDS &&
      !accountType
    ) {
      setAccountType('undefined');
    }
  }, [transactionInfo, baseellaStatus, accountType]);

  let content;

  const currentFromAccount = fromPspAccountsList?.find(
    (item) =>
      item.account?.fullNumber === fields?.senderAccountNumber ||
      item.account?.fullNumber === transactionInfo?.senderAccountNumber,
  );

  const currentToAccount = toPspAccountsList?.find(
    (item) =>
      item.account?.fullNumber === fields?.recipientAccountNumber ||
      item.account?.fullNumber === transactionInfo?.recipientAccountNumber,
  );

  const enableEdit = Boolean(control);

  if (isLoading) {
    content = (
      <div className={styles.loader}>
        <IconLoader className={styles.loaderIcon} />
      </div>
    );
  } else {
    content = (
      <div
        className={`${styles.transactionInfoWrapper} ${
          !isCreateForm ? styles.tabContentGeneral : ''
        }`}
      >
        <div className={styles.transactionBlockWrapper}>
          {transactionInfo && (
            <SidepanelDateRow
              title="Creation Date"
              value={
                enableEdit ? '' : formatDate(transactionInfo?.createdAt || '')
              }
              name="createdAt"
              placeholder="Select Date"
              control={control}
              enableEdit={enableEdit}
              copyEnabled={Boolean(transactionInfo)}
              copyValue={
                enableEdit
                  ? formatDate(fields?.createdAt || '')
                  : formatDate(transactionInfo?.createdAt || '')
              }
              tabIndex={-1}
              withTime
              disabled
            />
          )}
          <SidepanelDateRow
            title="Transfer Date"
            value={
              enableEdit ? '' : formatDate(transactionInfo?.transferDate || '')
            }
            name="transferDate"
            placeholder="Select Date"
            control={control}
            enableEdit={enableEdit}
            disabled={!isEditable || Boolean(transactionInfo)}
            error={errors?.transferDate?.message}
            tabIndex={!isEditable || Boolean(transactionInfo) ? -1 : 0}
            copyEnabled={Boolean(transactionInfo)}
            copyValue={
              enableEdit
                ? formatDate(fields?.transferDate || '')
                : formatDate(transactionInfo?.transferDate || '')
            }
            clearErrors={clearErrors}
            withTime={withTime?.transferDateWithTime}
            setWithTime={setWithTime?.setTransferDateWithTime}
          />
          <SidepanelDateRow
            title="Accepted Out"
            value={
              enableEdit ? '' : formatDate(transactionInfo?.acceptedOut || '')
            }
            name="acceptedOut"
            placeholder="Select Date"
            control={control}
            enableEdit={enableEdit}
            disabled={
              !isEditable ||
              baseellaStatus === BaseellaStatus.REJECTED ||
              baseellaStatus === BaseellaStatus.AML_REJ ||
              baseellaStatus === BaseellaStatus.ARCH
            }
            tabIndex={
              !isEditable ||
              baseellaStatus === BaseellaStatus.REJECTED ||
              baseellaStatus === BaseellaStatus.AML_REJ ||
              baseellaStatus === BaseellaStatus.ARCH
                ? -1
                : 0
            }
            error={errors?.acceptedOut?.message}
            copyEnabled={Boolean(transactionInfo)}
            copyValue={
              enableEdit
                ? formatDate(fields?.acceptedOut || '')
                : formatDate(transactionInfo?.acceptedOut || '')
            }
            clearErrors={clearErrors}
            withTime={withTime?.acceptedOutWithTime}
            setWithTime={setWithTime?.setAcceptedOutWithTime}
          />
          <SidepanelDateRow
            title="Accepted In"
            value={
              enableEdit ? '' : formatDate(transactionInfo?.acceptedIn || '')
            }
            name="acceptedIn"
            placeholder="Select Date"
            control={control}
            enableEdit={enableEdit}
            disabled={
              !isEditable ||
              baseellaStatus === BaseellaStatus.REJECTED ||
              baseellaStatus === BaseellaStatus.AML_REJ ||
              baseellaStatus === BaseellaStatus.ARCH
            }
            tabIndex={
              !isEditable ||
              baseellaStatus === BaseellaStatus.REJECTED ||
              baseellaStatus === BaseellaStatus.AML_REJ ||
              baseellaStatus === BaseellaStatus.ARCH
                ? -1
                : 0
            }
            error={errors?.acceptedIn?.message}
            copyEnabled={Boolean(transactionInfo)}
            copyValue={
              enableEdit
                ? formatDate(fields?.acceptedIn || '')
                : formatDate(transactionInfo?.acceptedIn || '')
            }
            clearErrors={clearErrors}
            withTime={withTime?.acceptedInWithTime}
            setWithTime={setWithTime?.setAcceptedInWithTime}
          />
          <div
            className={`${styles.amountRow} ${!control ? styles.noFlex : ''}`}
          >
            <div className={styles.amountRowItem}>
              <SidepanelAmountRow
                title="Amount"
                value={enableEdit ? '' : transactionInfo?.sum.toFixed(2)}
                name="sum"
                placeholder="Enter amount"
                amount={amount}
                setAmount={setAmount}
                control={control}
                enableEdit={enableEdit}
                disabled={!isEditable || Boolean(transactionInfo)}
                tabIndex={!isEditable || Boolean(transactionInfo) ? -1 : 0}
                error={errors?.sum?.message}
                currenciesList={currenciesList}
                currencyId={currencyId}
                copyEnabled={Boolean(transactionInfo)}
                copyValue={enableEdit ? fields?.sum : transactionInfo?.sum}
                clearErrors={clearErrors}
              />
            </div>
            <div className={styles.amountRowItem}>
              <SidepanelSelectRow
                title="Currency"
                value={enableEdit ? '' : transactionInfo?.currencyId}
                name="currencyId"
                placeholder="Choose currency"
                control={control}
                selectList={currencies}
                enableEdit={enableEdit}
                disabled={!isEditable || Boolean(transactionInfo)}
                tabIndex={!isEditable || Boolean(transactionInfo) ? -1 : 0}
                error={errors?.currencyId?.message}
                copyEnabled={Boolean(transactionInfo)}
                copyValue={
                  enableEdit
                    ? currencies?.find(
                        (item) => item.value === fields?.currencyId,
                      )?.name
                    : transactionInfo?.currency?.name
                }
                clearErrors={clearErrors}
              />
            </div>
          </div>
          <SidepanelSelectRow
            title="Payment Type"
            value={
              enableEdit
                ? ''
                : paymentsTypes?.find(
                    (item) => item.value === transactionInfo?.paymentType,
                  )?.name
            }
            name="paymentType"
            placeholder="Choose Payment Type"
            control={control}
            selectList={paymentsTypes}
            enableEdit={enableEdit}
            disabled={!isEditable || Boolean(transactionInfo) || !currencyId}
            tabIndex={
              !isEditable || Boolean(transactionInfo) || !currencyId ? -1 : 0
            }
            error={errors?.paymentType?.message}
            copyEnabled={Boolean(transactionInfo)}
            copyValue={
              enableEdit
                ? paymentsTypes?.find(
                    (item) => item.value === fields?.paymentType,
                  )?.name
                : paymentsTypes?.find(
                    (item) => item.value === transactionInfo?.paymentType,
                  )?.name
            }
            clearErrors={clearErrors}
            withClearButton
          />
          {paymentType && paymentType === PaymentType.CRYPTO_ASSET_PAYMENT && (
            <SidepanelSelectRow
              title="CASP"
              value={
                enableEdit
                  ? ''
                  : caspList?.find(
                      (item) => item.id === transactionInfo?.caspId,
                    )?.name
              }
              name="caspId"
              placeholder="Choose CASP"
              control={control}
              selectList={caspList?.map((item) => ({
                name: item.name,
                value: item.id,
              }))}
              enableEdit={enableEdit}
              disabled={!isEditable || Boolean(transactionInfo) || !currencyId}
              tabIndex={
                !isEditable || Boolean(transactionInfo) || !currencyId ? -1 : 0
              }
              error={errors?.caspId?.message}
              copyEnabled={Boolean(transactionInfo)}
              copyValue={
                enableEdit
                  ? caspList?.find((item) => item.id === fields?.caspId)?.name
                  : caspList?.find(
                      (item) => item.id === transactionInfo?.caspId,
                    )?.name
              }
              clearErrors={clearErrors}
              withClearButton
            />
          )}

          <div className={styles.accountsTypesSelectorWrapper}>
            <div className={styles.accountsTypesSelector}>
              <div className={styles.accountType}>
                <CheckboxInput
                  type="radio"
                  name="internal"
                  checked={accountType === 'internal'}
                  title="Internal"
                  disabled={
                    !isEditable ||
                    Boolean(
                      transactionInfo &&
                      baseellaStatus !== BaseellaStatus.UNDEFINED_FUNDS &&
                      fromAccounts?.find(
                        (item) => item.value === fields?.senderAccountNumber,
                      ),
                    )
                  }
                  onChange={() => {
                    setAccountType('internal');
                  }}
                />
              </div>
              <div className={styles.accountType}>
                <CheckboxInput
                  type="radio"
                  name="undefined"
                  checked={accountType === 'undefined'}
                  title="Undefined funds"
                  disabled={
                    !isEditable ||
                    Boolean(
                      transactionInfo &&
                      baseellaStatus !== BaseellaStatus.UNDEFINED_FUNDS &&
                      fromAccounts?.find(
                        (item) => item.value === fields?.senderAccountNumber,
                      ),
                    )
                  }
                  onChange={() => {
                    setAccountType('undefined');

                    if (setValue) {
                      setValue('fromPartnerPspId', '');
                    }
                  }}
                />
              </div>
            </div>
            {accountType === 'internal' && (
              <>
                <SidepanelAutocompleteRow
                  title="From Partner Bank"
                  value={enableEdit ? '' : transactionInfo?.fromPartnerPspId}
                  name="fromPartnerPspId"
                  placeholder="Choose From Partner Bank"
                  autocompleteList={fromPartnerPSPs}
                  isLoading={fromPartnerPSPsIsLoading}
                  control={control}
                  enableEdit={enableEdit}
                  disabled={
                    !isEditable ||
                    !paymentTypeIsFilled ||
                    Boolean(
                      transactionInfo &&
                      baseellaStatus !== BaseellaStatus.UNDEFINED_FUNDS,
                    )
                  }
                  tabIndex={
                    !isEditable ||
                    !paymentTypeIsFilled ||
                    Boolean(
                      transactionInfo &&
                      baseellaStatus !== BaseellaStatus.UNDEFINED_FUNDS,
                    )
                      ? -1
                      : 0
                  }
                  error={errors?.fromPartnerPspId?.message}
                  onSearchAutocomplete={onSearchAutocomplete}
                  copyEnabled={Boolean(transactionInfo)}
                  copyValue={
                    enableEdit
                      ? fromPartnerPSPs?.find(
                          (item) => item.value === fields?.fromPartnerPspId,
                        )?.name
                      : transactionInfo?.fromPartnerPspId
                  }
                  clearErrors={clearErrors}
                  withClearButton
                />
                <SidepanelAutocompleteRow
                  title="From Account"
                  value={enableEdit ? '' : transactionInfo?.senderAccountNumber}
                  name="senderAccountNumber"
                  placeholder="Choose From Account"
                  autocompleteList={fromAccounts}
                  isLoading={fromAccountsIsLoading}
                  control={control}
                  enableEdit={enableEdit}
                  disabled={
                    !isEditable ||
                    !fromPartnerPspIsFilled ||
                    !activePermission?.includes(PermissionEnum.PSP_LIST) ||
                    Boolean(
                      transactionInfo &&
                      baseellaStatus !== BaseellaStatus.UNDEFINED_FUNDS &&
                      fromAccounts?.find(
                        (item) => item.value === fields?.senderAccountNumber,
                      ),
                    )
                  }
                  tabIndex={
                    !isEditable ||
                    !fromPartnerPspIsFilled ||
                    !activePermission?.includes(PermissionEnum.PSP_LIST) ||
                    Boolean(
                      transactionInfo &&
                      baseellaStatus !== BaseellaStatus.UNDEFINED_FUNDS &&
                      fromAccounts?.find(
                        (item) => item.value === fields?.senderAccountNumber,
                      ),
                    )
                      ? -1
                      : 0
                  }
                  error={
                    errors?.senderAccountNumber?.message ||
                    (isCreateForm &&
                    !activePermission?.includes(PermissionEnum.PSP_LIST)
                      ? 'Data not loaded. Permission denied'
                      : '')
                  }
                  onSearchAutocomplete={onSearchAutocomplete}
                  copyEnabled={Boolean(transactionInfo)}
                  copyValue={
                    enableEdit
                      ? fromAccounts?.find(
                          (item) => item.value === fields?.senderAccountNumber,
                        )?.name
                      : transactionInfo?.senderAccountNumber
                  }
                  pagination={fromPspAccountsPagination}
                  nextPageIsLoading={fromPspAccountsNextPageIsLoading}
                  onScroll={fromPspAccountsOnScroll}
                  clearErrors={clearErrors}
                  withInfiniteScroll
                  withSuffix
                />
              </>
            )}
            {accountType === 'undefined' && (
              <SidepanelTextRow
                title="From Account"
                value={enableEdit ? '' : transactionInfo?.senderAccountNumber}
                name="senderAccountNumber"
                placeholder="Enter Partner PSP Account"
                control={control}
                fieldValue={accountNumber}
                setFieldValue={setAccountNumber}
                enableEdit={enableEdit}
                tabIndex={
                  !isEditable ||
                  Boolean(
                    transactionInfo &&
                    baseellaStatus !== BaseellaStatus.UNDEFINED_FUNDS &&
                    fromAccounts?.find(
                      (item) => item.value === fields?.senderAccountNumber,
                    ),
                  )
                    ? -1
                    : 0
                }
                disabled={
                  !isEditable ||
                  Boolean(
                    transactionInfo &&
                    baseellaStatus !== BaseellaStatus.UNDEFINED_FUNDS &&
                    fromAccounts?.find(
                      (item) => item.value === fields?.senderAccountNumber,
                    ),
                  )
                }
                error={errors?.senderAccountNumber?.message}
                copyEnabled={Boolean(transactionInfo)}
                copyValue={
                  enableEdit
                    ? fields?.senderAccountNumber
                    : transactionInfo?.senderAccountNumber
                }
                hasExternalAccountValue={Boolean(
                  fields?.pspIntegrationAccountId,
                )}
                clearErrors={clearErrors}
              />
            )}
          </div>

          {control && (
            <>
              {(!overdraftEnabled ||
                (currentFromAccount &&
                  (!currentFromAccount.account.overdraftEnabled ||
                    !fromAccountBalanceInfo?.overdraftEnabled))) && (
                <div
                  className={`${styles.balanceWrapper} ${
                    styles.nostroBalanceWrapper
                  } ${transactionInfo ? styles.balanceWrapperWithCopy : ''}`}
                >
                  <InputText
                    disabled
                    tabIndex={-1}
                    name="balance"
                    placeholder=""
                    title="Current balance"
                    value={
                      fromAccountBalance !== undefined
                        ? `${formatBalance(
                            fromAccountBalance,
                            currenciesList?.find(
                              (item) => item.id === currencyId,
                            ),
                          )} ${
                            currencies?.find(
                              (item) => item.value === currencyId,
                            )?.name
                          }`
                        : '-'
                    }
                    error={
                      !isCreateForm &&
                      !activePermission?.includes(PermissionEnum.PSP_LIST)
                        ? 'Data not loaded. Permission denied'
                        : ''
                    }
                  />
                  <ButtonPseudo
                    icon={<IconRefresh />}
                    onClick={() =>
                      updateFromAccountBalance && updateFromAccountBalance()
                    }
                    className={`${styles.updateBalanceButton} ${
                      balanceFromAccountIsLoading ? styles.loading : ''
                    } `}
                    disabled={fromAccountBalance === undefined || !isEditable}
                    tabIndex={-1}
                  />
                  {Boolean(transactionInfo) && (
                    <button
                      type="button"
                      className={`${styles.copyButton} ${
                        styles.autocompleteCopyButton
                      } ${copyIsSuccess ? styles.success : ''}`}
                      onClick={() =>
                        onCopy(
                          fromAccountBalance !== undefined
                            ? fromAccountBalance?.toString()
                            : '-',
                        )
                      }
                      aria-label="Copy"
                    >
                      <IconCopy />
                    </button>
                  )}
                </div>
              )}
              {overdraftEnabled &&
                currentFromAccount?.account.overdraftEnabled &&
                fromAccountBalanceInfo?.overdraftEnabled && (
                  <div
                    className={`${styles.overdraftBalanceWrapper} ${styles.withMargin}`}
                  >
                    <div
                      className={`${styles.overdraftBalanceValue} ${
                        transactionInfo ? styles.withCopy : ''
                      }`}
                    >
                      <InputText
                        disabled
                        tabIndex={-1}
                        name="pspPartnerBalance"
                        placeholder=""
                        title="Total available balance"
                        className={styles.overdraftBalance}
                        value={
                          fromAccountBalance !== undefined
                            ? `${formatBalance(
                                fromAccountBalance,
                                currenciesList?.find(
                                  (item) => item.id === currencyId,
                                ),
                              )} ${
                                currencies?.find(
                                  (item) => item.value === currencyId,
                                )?.name
                              }`
                            : '-'
                        }
                      />
                      <ButtonPseudo
                        icon={<IconRefresh />}
                        onClick={() =>
                          updateFromAccountBalance && updateFromAccountBalance()
                        }
                        className={`${styles.updateBalanceButton} ${
                          balanceFromAccountIsLoading ? styles.loading : ''
                        } `}
                        disabled={
                          fromAccountBalance === undefined || !isEditable
                        }
                        tabIndex={-1}
                      />
                      {Boolean(transactionInfo) && (
                        <button
                          type="button"
                          className={`${styles.copyButton} ${
                            styles.autocompleteCopyButton
                          } ${copyIsSuccess ? styles.success : ''}`}
                          onClick={() =>
                            onCopy(
                              fromAccountBalance !== undefined
                                ? fromAccountBalance?.toString()
                                : '-',
                            )
                          }
                          aria-label="Copy"
                        >
                          <IconCopy />
                        </button>
                      )}
                    </div>
                    <div className={styles.overdraftBalanceValue}>
                      <InputText
                        disabled
                        tabIndex={-1}
                        name="pspPartnerAvailableOverdraft"
                        placeholder=""
                        title="Remaining overdraft"
                        value={
                          fromAccountBalanceInfo &&
                          fromAccountBalanceInfo?.overdraftEnabled
                            ? `${formatBalance(
                                // eslint-disable-next-line no-nested-ternary
                                fromAccountBalanceInfo?.value < 0
                                  ? (fromAccountBalanceInfo?.overdraftValue as number) -
                                      Math.abs(fromAccountBalanceInfo?.value) <
                                    0
                                    ? 0
                                    : (fromAccountBalanceInfo?.overdraftValue as number) -
                                      Math.abs(fromAccountBalanceInfo?.value)
                                  : (fromAccountBalanceInfo?.overdraftValue as number),
                                currenciesList?.find(
                                  (item) => item.id === currencyId,
                                ),
                              )} ${
                                currencies?.find(
                                  (item) => item.value === currencyId,
                                )?.name
                              }`
                            : '-'
                        }
                      />
                    </div>
                    <div className={styles.overdraftBalanceValue}>
                      <InputText
                        disabled
                        tabIndex={-1}
                        name="pspPartnerOverdraftLimit"
                        placeholder=""
                        title="Overdraft limit"
                        value={
                          fromAccountBalanceInfo &&
                          fromAccountBalanceInfo?.overdraftEnabled
                            ? `${formatBalance(
                                fromAccountBalanceInfo?.overdraftValue as number,
                                currenciesList?.find(
                                  (item) => item.id === currencyId,
                                ),
                              )} ${
                                currencies?.find(
                                  (item) => item.value === currencyId,
                                )?.name
                              }`
                            : '-'
                        }
                      />
                    </div>
                  </div>
                )}
            </>
          )}
          {!control && (
            <>
              {(!overdraftEnabled ||
                (currentFromAccount &&
                  (!currentFromAccount.account.overdraftEnabled ||
                    !fromAccountBalanceInfo?.overdraftEnabled))) && (
                <SidepanelDefaultRow
                  title="Total available balance"
                  valueClassName={
                    transactionInfo ? styles.readCopyButtonWrapper : ''
                  }
                >
                  {fromAccountBalance !== undefined
                    ? `${formatBalance(
                        fromAccountBalance,
                        currenciesList?.find((item) => item.id === currencyId),
                      )} ${transactionInfo?.currency?.name}`
                    : '-'}
                  {Boolean(transactionInfo) && (
                    <button
                      type="button"
                      className={`${styles.readCopyButton} ${
                        copyIsSuccess ? styles.success : ''
                      }`}
                      onClick={() =>
                        onCopy(
                          fromAccountBalance !== undefined
                            ? fromAccountBalance?.toString()
                            : '-',
                        )
                      }
                      aria-label="Copy"
                    >
                      <IconCopy />
                    </button>
                  )}
                </SidepanelDefaultRow>
              )}
              {overdraftEnabled &&
                currentFromAccount?.account.overdraftEnabled &&
                fromAccountBalanceInfo?.overdraftEnabled && (
                  <>
                    <SidepanelDefaultRow
                      title="Total available balance"
                      valueClassName={
                        transactionInfo ? styles.readCopyButtonWrapper : ''
                      }
                    >
                      {fromAccountBalance !== undefined
                        ? `${formatBalance(
                            fromAccountBalance,
                            currenciesList?.find(
                              (item) => item.id === currencyId,
                            ),
                          )} ${transactionInfo?.currency?.name}`
                        : '-'}
                      {Boolean(transactionInfo) && (
                        <button
                          type="button"
                          className={`${styles.readCopyButton} ${
                            copyIsSuccess ? styles.success : ''
                          }`}
                          onClick={() =>
                            onCopy(
                              fromAccountBalance !== undefined
                                ? fromAccountBalance?.toString()
                                : '-',
                            )
                          }
                          aria-label="Copy"
                        >
                          <IconCopy />
                        </button>
                      )}
                    </SidepanelDefaultRow>
                    <SidepanelDefaultRow title="Remaining overdraft">
                      {fromAccountBalanceInfo &&
                      fromAccountBalanceInfo?.overdraftEnabled
                        ? `${formatBalance(
                            // eslint-disable-next-line no-nested-ternary
                            fromAccountBalanceInfo?.value < 0
                              ? (fromAccountBalanceInfo?.overdraftValue as number) -
                                  Math.abs(fromAccountBalanceInfo?.value) <
                                0
                                ? 0
                                : (fromAccountBalanceInfo?.overdraftValue as number) -
                                  Math.abs(fromAccountBalanceInfo?.value)
                              : (fromAccountBalanceInfo?.overdraftValue as number),
                            currenciesList?.find(
                              (item) => item.id === currencyId,
                            ),
                          )} ${
                            currencies?.find(
                              (item) => item.value === currencyId,
                            )?.name
                          }`
                        : '-'}
                    </SidepanelDefaultRow>
                    <SidepanelDefaultRow title="Overdraft Limit">
                      {fromAccountBalanceInfo &&
                      fromAccountBalanceInfo?.overdraftEnabled
                        ? `${formatBalance(
                            fromAccountBalanceInfo?.overdraftValue as number,
                            currenciesList?.find(
                              (item) => item.id === currencyId,
                            ),
                          )} ${
                            currencies?.find(
                              (item) => item.value === currencyId,
                            )?.name
                          }`
                        : '-'}
                    </SidepanelDefaultRow>
                  </>
                )}
            </>
          )}
          <SidepanelAutocompleteRow
            title="To Partner Bank"
            value={enableEdit ? '' : transactionInfo?.toPartnerPspId}
            name="toPartnerPspId"
            placeholder="Choose To Partner Bank"
            autocompleteList={toPartnerPSPs}
            isLoading={toPartnerPSPsIsLoading}
            control={control}
            enableEdit={enableEdit}
            disabled={
              !isEditable || !paymentTypeIsFilled || Boolean(transactionInfo)
            }
            tabIndex={
              !isEditable || !paymentTypeIsFilled || Boolean(transactionInfo)
                ? -1
                : 0
            }
            error={errors?.toPartnerPspId?.message}
            onSearchAutocomplete={onSearchAutocomplete}
            copyEnabled={Boolean(transactionInfo)}
            copyValue={
              enableEdit
                ? toPartnerPSPs?.find(
                    (item) => item.value === fields?.toPartnerPspId,
                  )?.name
                : transactionInfo?.toPartnerPspId
            }
            clearErrors={clearErrors}
            withClearButton
          />
          <SidepanelAutocompleteRow
            title="To Account"
            value={enableEdit ? '' : transactionInfo?.recipientAccountNumber}
            name="recipientAccountNumber"
            placeholder="Choose To Account"
            autocompleteList={toAccounts}
            isLoading={toAccountsIsLoading}
            control={control}
            enableEdit={enableEdit}
            disabled={
              !isEditable ||
              !toPartnerPspIsFilled ||
              Boolean(transactionInfo) ||
              !activePermission?.includes(PermissionEnum.PSP_LIST)
            }
            tabIndex={
              !isEditable ||
              !toPartnerPspIsFilled ||
              Boolean(transactionInfo) ||
              !activePermission?.includes(PermissionEnum.PSP_LIST)
                ? -1
                : 0
            }
            error={
              errors?.recipientAccountNumber?.message ||
              (isCreateForm &&
              !activePermission?.includes(PermissionEnum.PSP_LIST)
                ? 'Data not loaded. Permission denied'
                : '')
            }
            copyEnabled={Boolean(transactionInfo)}
            copyValue={
              enableEdit
                ? toAccounts?.find(
                    (item) => item.value === fields?.recipientAccountNumber,
                  )?.name
                : transactionInfo?.recipientAccountNumber
            }
            onSearchAutocomplete={onSearchAutocomplete}
            pagination={toPspAccountsPagination}
            nextPageIsLoading={toPspAccountsNextPageIsLoading}
            onScroll={toPspAccountsOnScroll}
            clearErrors={clearErrors}
            withInfiniteScroll
            withSuffix
          />

          {control && (
            <>
              {(!overdraftEnabled ||
                (currentToAccount &&
                  (!currentToAccount.account.overdraftEnabled ||
                    !toAccountBalanceInfo?.overdraftEnabled))) && (
                <div
                  className={`${styles.balanceWrapper} ${
                    styles.nostroBalanceWrapper
                  } ${transactionInfo ? styles.balanceWrapperWithCopy : ''}`}
                >
                  <InputText
                    disabled
                    tabIndex={-1}
                    name="balance"
                    placeholder=""
                    title="Current balance"
                    value={
                      toAccountBalance !== undefined
                        ? `${formatBalance(
                            toAccountBalance,
                            currenciesList?.find(
                              (item) => item.id === currencyId,
                            ),
                          )} ${
                            currencies?.find(
                              (item) => item.value === currencyId,
                            )?.name
                          }`
                        : '-'
                    }
                    error={
                      !isCreateForm &&
                      !activePermission?.includes(PermissionEnum.PSP_LIST)
                        ? 'Data not loaded. Permission denied'
                        : ''
                    }
                  />
                  <ButtonPseudo
                    icon={<IconRefresh />}
                    onClick={() =>
                      updateToAccountBalance && updateToAccountBalance()
                    }
                    className={`${styles.updateBalanceButton} ${
                      balanceToAccountIsLoading ? styles.loading : ''
                    } `}
                    disabled={toAccountBalance === undefined || !isEditable}
                    tabIndex={-1}
                  />
                  {Boolean(transactionInfo) && (
                    <button
                      type="button"
                      className={`${styles.copyButton} ${
                        styles.autocompleteCopyButton
                      } ${copyIsSuccess ? styles.success : ''}`}
                      onClick={() =>
                        onCopy(
                          toAccountBalance !== undefined
                            ? toAccountBalance?.toString()
                            : '-',
                        )
                      }
                      aria-label="Copy"
                    >
                      <IconCopy />
                    </button>
                  )}
                </div>
              )}
              {overdraftEnabled &&
                currentToAccount?.account.overdraftEnabled &&
                toAccountBalanceInfo?.overdraftEnabled && (
                  <div
                    className={`${styles.overdraftBalanceWrapper} ${styles.withMargin}`}
                  >
                    <div
                      className={`${styles.overdraftBalanceValue} ${
                        transactionInfo ? styles.withCopy : ''
                      }`}
                    >
                      <InputText
                        disabled
                        tabIndex={-1}
                        name="pspPartnerBalance"
                        placeholder=""
                        title="Total available balance"
                        className={styles.overdraftBalance}
                        value={
                          toAccountBalance !== undefined
                            ? `${formatBalance(
                                toAccountBalance,
                                currenciesList?.find(
                                  (item) => item.id === currencyId,
                                ),
                              )} ${
                                currencies?.find(
                                  (item) => item.value === currencyId,
                                )?.name
                              }`
                            : '-'
                        }
                      />
                      <ButtonPseudo
                        icon={<IconRefresh />}
                        onClick={() =>
                          updateToAccountBalance && updateToAccountBalance()
                        }
                        className={`${styles.updateBalanceButton} ${
                          balanceToAccountIsLoading ? styles.loading : ''
                        } `}
                        disabled={toAccountBalance === undefined || !isEditable}
                        tabIndex={-1}
                      />
                      {Boolean(transactionInfo) && (
                        <button
                          type="button"
                          className={`${styles.copyButton} ${
                            styles.autocompleteCopyButton
                          } ${copyIsSuccess ? styles.success : ''}`}
                          onClick={() =>
                            onCopy(
                              toAccountBalance !== undefined
                                ? toAccountBalance?.toString()
                                : '-',
                            )
                          }
                          aria-label="Copy"
                        >
                          <IconCopy />
                        </button>
                      )}
                    </div>
                    <div className={styles.overdraftBalanceValue}>
                      <InputText
                        disabled
                        tabIndex={-1}
                        name="pspPartnerAvailableOverdraft"
                        placeholder=""
                        title="Remaining overdraft"
                        value={
                          toAccountBalanceInfo &&
                          toAccountBalanceInfo?.overdraftEnabled
                            ? `${formatBalance(
                                // eslint-disable-next-line no-nested-ternary
                                toAccountBalanceInfo?.value < 0
                                  ? (toAccountBalanceInfo?.overdraftValue as number) -
                                      Math.abs(toAccountBalanceInfo?.value) <
                                    0
                                    ? 0
                                    : (toAccountBalanceInfo?.overdraftValue as number) -
                                      Math.abs(toAccountBalanceInfo?.value)
                                  : (toAccountBalanceInfo?.overdraftValue as number),
                                currenciesList?.find(
                                  (item) => item.id === currencyId,
                                ),
                              )} ${
                                currencies?.find(
                                  (item) => item.value === currencyId,
                                )?.name
                              }`
                            : '-'
                        }
                      />
                    </div>
                    <div className={styles.overdraftBalanceValue}>
                      <InputText
                        disabled
                        tabIndex={-1}
                        name="pspPartnerOverdraftLimit"
                        placeholder=""
                        title="Overdraft limit"
                        value={
                          toAccountBalanceInfo &&
                          toAccountBalanceInfo?.overdraftEnabled
                            ? `${formatBalance(
                                toAccountBalanceInfo?.overdraftValue as number,
                                currenciesList?.find(
                                  (item) => item.id === currencyId,
                                ),
                              )} ${
                                currencies?.find(
                                  (item) => item.value === currencyId,
                                )?.name
                              }`
                            : '-'
                        }
                      />
                    </div>
                  </div>
                )}
            </>
          )}
          {purposeCodeVisible && paymentType && (
            <>
              <div className={styles.purposeCodeSeparator} />
              <SidepanelAutocompleteRow
                title="Purpose code"
                value={enableEdit ? '' : transactionInfo?.purposeCode}
                name="purposeCode"
                placeholder="Select purpose code"
                autocompleteList={purposeCodes || []}
                isLoading={purposeCodesIsLoading}
                control={control}
                enableEdit={enableEdit}
                disabled={
                  !isEditable || Boolean(transactionId && transactionInfo)
                }
                tabIndex={
                  !isEditable ||
                  !activePermission?.includes(PermissionEnum.PSP_LIST) ||
                  Boolean(transactionId && transactionInfo)
                    ? -1
                    : 0
                }
                error={errors?.purposeCode?.message}
                onSearchAutocomplete={onSearchAutocomplete}
                copyValue={
                  enableEdit
                    ? fields?.purposeCode
                    : transactionInfo?.purposeCode
                }
                copyEnabled={Boolean(transactionId && transactionInfo)}
                pagination={purposeCodesPagination}
                onScroll={onScrollPurposeCodes}
                nextPageIsLoading={purposeCodesNextPageIsLoading}
                clearErrors={clearErrors}
                withInfiniteScroll
                withSuffix
              />
            </>
          )}
          {!control && (
            <>
              {(!overdraftEnabled ||
                (currentToAccount &&
                  (!currentToAccount.account.overdraftEnabled ||
                    !toAccountBalanceInfo?.overdraftEnabled))) && (
                <SidepanelDefaultRow
                  title="Current balance"
                  valueClassName={
                    transactionInfo ? styles.readCopyButtonWrapper : ''
                  }
                >
                  {toAccountBalance !== undefined
                    ? `${formatBalance(
                        toAccountBalance,
                        currenciesList?.find((item) => item.id === currencyId),
                      )} ${
                        currencies?.find((item) => item.value === currencyId)
                          ?.name
                      }`
                    : '-'}
                  {Boolean(transactionInfo) && (
                    <button
                      type="button"
                      className={`${styles.readCopyButton} ${
                        copyIsSuccess ? styles.success : ''
                      }`}
                      onClick={() =>
                        onCopy(
                          toAccountBalance !== undefined
                            ? toAccountBalance?.toString()
                            : '-',
                        )
                      }
                      aria-label="Copy"
                    >
                      <IconCopy />
                    </button>
                  )}
                </SidepanelDefaultRow>
              )}
              {overdraftEnabled &&
                currentFromAccount?.account.overdraftEnabled &&
                toAccountBalanceInfo?.overdraftEnabled && (
                  <>
                    <SidepanelDefaultRow
                      title="Total available balance"
                      valueClassName={
                        transactionInfo ? styles.readCopyButtonWrapper : ''
                      }
                    >
                      {toAccountBalance !== undefined
                        ? `${formatBalance(
                            toAccountBalance,
                            currenciesList?.find(
                              (item) => item.id === currencyId,
                            ),
                          )} ${
                            currencies?.find(
                              (item) => item.value === currencyId,
                            )?.name
                          }`
                        : '-'}
                      {Boolean(transactionInfo) && (
                        <button
                          type="button"
                          className={`${styles.readCopyButton} ${
                            copyIsSuccess ? styles.success : ''
                          }`}
                          onClick={() =>
                            onCopy(
                              toAccountBalance !== undefined
                                ? toAccountBalance?.toString()
                                : '-',
                            )
                          }
                          aria-label="Copy"
                        >
                          <IconCopy />
                        </button>
                      )}
                    </SidepanelDefaultRow>
                    <SidepanelDefaultRow title="Remaining overdraft">
                      {toAccountBalanceInfo &&
                      toAccountBalanceInfo?.overdraftEnabled
                        ? `${formatBalance(
                            // eslint-disable-next-line no-nested-ternary
                            toAccountBalanceInfo?.value < 0
                              ? (toAccountBalanceInfo?.overdraftValue as number) -
                                  Math.abs(toAccountBalanceInfo?.value) <
                                0
                                ? 0
                                : (toAccountBalanceInfo?.overdraftValue as number) -
                                  Math.abs(toAccountBalanceInfo?.value)
                              : (toAccountBalanceInfo?.overdraftValue as number),
                            currenciesList?.find(
                              (item) => item.id === currencyId,
                            ),
                          )} ${
                            currencies?.find(
                              (item) => item.value === currencyId,
                            )?.name
                          }`
                        : '-'}
                    </SidepanelDefaultRow>
                    <SidepanelDefaultRow title="Overdraft Limit">
                      {toAccountBalanceInfo &&
                      toAccountBalanceInfo?.overdraftEnabled
                        ? `${formatBalance(
                            toAccountBalanceInfo?.overdraftValue as number,
                            currenciesList?.find(
                              (item) => item.id === currencyId,
                            ),
                          )} ${
                            currencies?.find(
                              (item) => item.value === currencyId,
                            )?.name
                          }`
                        : '-'}
                    </SidepanelDefaultRow>
                  </>
                )}
            </>
          )}
          {!transactionId && control && (
            <DisableCreationOption
              control={control}
              clearErrors={clearErrors}
            />
          )}
          <SidepanelTextRow
            title="Document"
            value={enableEdit ? '' : transactionInfo?.documentNumber}
            name="documentNumber"
            placeholder="Enter Document"
            control={control}
            enableEdit={enableEdit}
            disabled={!isEditable || Boolean(transactionInfo)}
            tabIndex={!isEditable || Boolean(transactionInfo) ? -1 : 0}
            error={errors?.documentNumber?.message}
            copyEnabled={Boolean(transactionInfo)}
            copyValue={
              enableEdit
                ? fields?.documentNumber
                : transactionInfo?.documentNumber
            }
            clearErrors={clearErrors}
          />
        </div>

        <div className={styles.transactionBlockWrapper}>
          <SidepanelTextareaRow
            title="Payment Details"
            value={enableEdit ? '' : transactionInfo?.info}
            name="info"
            placeholder="Enter Payment Details"
            control={control}
            enableEdit={enableEdit}
            disabled={!isEditable || Boolean(transactionInfo)}
            tabIndex={!isEditable || Boolean(transactionInfo) ? -1 : 0}
            error={errors?.info?.message}
            copyEnabled={Boolean(transactionInfo)}
            copyValue={enableEdit ? fields?.info : transactionInfo?.info}
            fieldValue={infoValue}
            setFieldValue={setInfoValue}
            maxLength={140}
            clearErrors={clearErrors}
          />
        </div>
      </div>
    );
  }

  return content;
}
